import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITES, STATUS_COLOR, STATUS_LABEL } from './demoData';
import {
    calcCentroid,
    getMoistStatus,
    getSiteWeather,
    MOIST_COLOR,
    MOIST_LABEL,
    WEATHER_ICON,
    WEATHER_LABEL,
    treeEmoji,
    type MoistStatus,
    type SavedSite,
} from './demoStorage';


const COORDS = [
    { lat: 37.517, lng: 127.0473 },
    { lat: 37.504, lng: 127.0127 },
    { lat: 37.5219, lng: 127.104 },
    { lat: 37.5318, lng: 126.9703 },
    { lat: 37.5126, lng: 127.1026 },
    { lat: 37.5571, lng: 126.9239 },
    { lat: 37.608, lng: 126.9277 },
    { lat: 37.5491, lng: 127.1239 },
];

/* ── HTML templates ── */

const markerHtml = (color: string, treeIco = '🌳') => `
    <div style="position:relative;width:34px;height:34px;cursor:pointer;">
        <div style="position:absolute;inset:0;background:${color};
            border:2.5px solid rgba(255,255,255,0.95);
            border-radius:50% 50% 0 50%;transform:rotate(-45deg);
            box-shadow:0 2px 12px rgba(0,0,0,0.32);"></div>
        <div style="position:absolute;inset:0;display:flex;align-items:center;
            justify-content:center;font-size:15px;padding-bottom:4px;">${treeIco}</div>
    </div>`;

const vtxHtml = (canClose: boolean) => `
    <div style="width:18px;height:18px;
        background:${canClose ? '#1E8B53' : '#fff'};
        border:3px solid #1E8B53;border-radius:50%;
        cursor:${canClose ? 'pointer' : 'crosshair'};
        box-shadow:0 2px 8px rgba(30,139,83,0.5);
        ${canClose ? 'animation:twPulse 1s ease-in-out infinite;' : ''}"></div>`;

const midVtxHtml = () => `
    <div style="width:12px;height:12px;background:#fff;
        border:2.5px solid #1E8B53;border-radius:50%;
        cursor:crosshair;box-shadow:0 1px 4px rgba(0,0,0,0.25);"></div>`;

const iwHtml = (s: (typeof SITES)[0]) => {
    const c = STATUS_COLOR[s.status];
    const tIco = treeEmoji(s.trees);
    return `
        <div style="background:#fff;border-radius:13px;padding:16px;min-width:230px;
            font-family:Pretendard,-apple-system,sans-serif;
            box-shadow:0 8px 30px rgba(0,0,0,0.14);border:1.5px solid #E2E8E2;position:relative;">
            <button onclick="window.__twIwClose()" style="position:absolute;top:10px;right:10px;
                background:none;border:none;color:#8AA88A;font-size:16px;cursor:pointer;line-height:1;padding:2px;">✕</button>
            <div style="font-size:13px;font-weight:800;color:#1A2B1A;margin-bottom:2px;padding-right:22px">${s.name}</div>
            <div style="font-size:11px;color:#8AA88A;margin-bottom:8px">${s.addr} · ${s.area}㎡</div>
            <div style="background:#F0FDF4;border:1px solid #BBF7D0;border-radius:9px;
                padding:8px 11px;margin-bottom:8px;display:flex;align-items:center;gap:8px;">
                <span style="font-size:26px;line-height:1;flex-shrink:0">${tIco}</span>
                <div style="flex:1;min-width:0">
                    <div style="font-size:9.5px;color:#15803D;font-weight:600;margin-bottom:1px">식재 수종</div>
                    <div style="font-size:13px;font-weight:800;color:#166534;
                        white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${s.trees}</div>
                </div>
                <div style="text-align:right;flex-shrink:0">
                    <div style="font-size:9.5px;color:#15803D;font-weight:600;margin-bottom:1px">총 그루</div>
                    <div style="font-size:13px;font-weight:800;color:#166534">${s.total}</div>
                </div>
            </div>
            <div style="background:#F5F7F5;border-radius:9px;padding:10px 12px;margin-bottom:10px">
                <div style="display:flex;justify-content:space-between;font-size:11.5px;margin-bottom:5px">
                    <span style="color:#8AA88A">토양 수분율</span>
                    <span style="font-weight:800;color:${c}">${s.moist}%</span>
                </div>
                <div style="height:5px;background:#E2E8E2;border-radius:3px;overflow:hidden;margin-bottom:6px">
                    <div style="height:100%;width:${s.moist}%;background:${c};border-radius:3px"></div>
                </div>
                <div style="display:flex;justify-content:space-between;font-size:11.5px">
                    <span style="color:#8AA88A">상태</span>
                    <span style="font-weight:700;color:${c}">${STATUS_LABEL[s.status]}</span>
                </div>
            </div>
            <div style="display:flex;justify-content:space-between;font-size:11px;color:#8AA88A;margin-bottom:12px">
                <span>👤 ${s.mgr}</span><span>마지막 관수 ${s.days}일전</span>
            </div>
            <button onclick="window.__twNav('${encodeURIComponent(s.name)}')" style="
                width:100%;padding:8px;background:#1E8B53;color:#fff;border:none;
                border-radius:8px;font-size:12.5px;font-weight:700;cursor:pointer;">상세 보기 →</button>
        </div>`;
};

const savedSiteLabelHtml = (name: string, area: string, moist: number, temp: number, weatherIcon: string, treeName: string) => {
    const st = getMoistStatus(moist);
    const color = MOIST_COLOR[st];
    const label = MOIST_LABEL[st];
    const short = (name || '미입력 단지').split(' ').slice(0, 2).join(' ');
    const tIco = treeEmoji(treeName);
    const treeLabel = treeName || '수종 미입력';
    return `
        <div style="background:rgba(255,255,255,0.96);border:2px solid ${color};
            border-radius:8px;padding:4px 9px;
            font-size:10.5px;font-weight:800;color:#1A2B1A;
            white-space:nowrap;font-family:Pretendard,-apple-system,sans-serif;
            box-shadow:0 2px 8px rgba(0,0,0,0.13);line-height:1.6;cursor:pointer;">
            <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
                <span>${short}</span>
                <span style="font-size:11px;font-weight:700;color:#374151;">${weatherIcon} ${temp}°C</span>
            </div>
            <div style="display:flex;align-items:center;justify-content:space-between;gap:6px;">
                <span style="color:${color};font-weight:700;font-size:9.5px">${moist}% · ${label}</span>
                <span style="color:#9CA3AF;font-size:9px">${area || '-'}㎡</span>
            </div>
            <div style="display:flex;align-items:center;gap:3px;margin-top:1px;">
                <span style="font-size:11px">${tIco}</span>
                <span style="font-size:9.5px;color:#166534;font-weight:700">${treeLabel}</span>
            </div>
        </div>`;
};

const savedSiteIwHtml = (site: SavedSite) => {
    const f = site.form;
    const moist = site.moist ?? 50;
    const st = getMoistStatus(moist);
    const color = MOIST_COLOR[st];
    const label = MOIST_LABEL[st];
    const date = new Date(site.createdAt).toLocaleDateString('ko-KR');
    const { temp, weather } = getSiteWeather(site);
    const wIcon = WEATHER_ICON[weather];
    const wLabel = WEATHER_LABEL[weather];
    return `
        <div style="background:#fff;border-radius:13px;padding:16px;min-width:220px;
            font-family:Pretendard,-apple-system,sans-serif;
            box-shadow:0 8px 30px rgba(0,0,0,0.14);border:1.5px solid #E2E8E2;position:relative;">
            <button onclick="window.__twIwClose()" style="position:absolute;top:10px;right:10px;
                background:none;border:none;color:#8AA88A;font-size:16px;cursor:pointer;line-height:1;padding:2px;">✕</button>
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
                <span style="display:inline-block;background:#D0F5E5;color:#166840;
                    font-size:9.5px;font-weight:700;border-radius:4px;padding:1px 6px;">등록 단지</span>
            </div>
            <div style="font-size:13px;font-weight:800;color:#1A2B1A;margin-bottom:2px;padding-right:22px">${f.name || '(이름 미입력)'}</div>
            <div style="font-size:11px;color:#8AA88A;margin-bottom:8px">${f.addr || '-'}</div>
            <div style="background:#F0FDF4;border:1px solid #BBF7D0;border-radius:9px;
                padding:8px 11px;margin-bottom:8px;display:flex;align-items:center;gap:8px;">
                <span style="font-size:26px;line-height:1;flex-shrink:0">${treeEmoji(f.treeName)}</span>
                <div style="flex:1;min-width:0">
                    <div style="font-size:9.5px;color:#15803D;font-weight:600;margin-bottom:1px">식재 수종</div>
                    <div style="font-size:13px;font-weight:800;color:#166534;
                        white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${f.treeName || '미입력'}</div>
                </div>
                <div style="text-align:right;flex-shrink:0">
                    <div style="font-size:9.5px;color:#15803D;font-weight:600;margin-bottom:1px">그루수</div>
                    <div style="font-size:13px;font-weight:800;color:#166534">${f.treeCount || '-'}</div>
                </div>
            </div>
            <div style="background:#F0F9FF;border-radius:9px;padding:9px 12px;margin-bottom:8px;
                display:flex;align-items:center;justify-content:space-between;font-size:12px;">
                <span style="font-size:22px;line-height:1">${wIcon}</span>
                <div style="text-align:right">
                    <div style="font-size:17px;font-weight:800;color:#1E3A5F;line-height:1.2">${temp}°C</div>
                    <div style="font-size:10px;color:#64748B;margin-top:1px">${wLabel}</div>
                </div>
            </div>
            <div style="background:#F5F7F5;border-radius:9px;padding:10px 12px;margin-bottom:10px;font-size:11.5px;display:flex;flex-direction:column;gap:5px;">
                <div style="display:flex;justify-content:space-between">
                    <span style="color:#8AA88A">수분율 (데모)</span>
                    <span style="font-weight:800;color:${color}">${moist}% · ${label}</span>
                </div>
                <div style="height:5px;background:#E2E8E2;border-radius:3px;overflow:hidden">
                    <div style="height:100%;width:${Math.max(2, moist)}%;background:${color};border-radius:3px"></div>
                </div>
                <div style="display:flex;justify-content:space-between;margin-top:2px">
                    <span style="color:#8AA88A">면적</span>
                    <span style="font-weight:700">${f.area || '-'} ㎡ (${f.pyeong || '-'}평)</span>
                </div>
                <div style="display:flex;justify-content:space-between">
                    <span style="color:#8AA88A">담당자</span>
                    <span style="font-weight:700">👤 ${f.mgr || '-'}</span>
                </div>
                <div style="display:flex;justify-content:space-between">
                    <span style="color:#8AA88A">꼭지점</span>
                    <span style="font-weight:700">${site.polygon.length}개</span>
                </div>
            </div>
            <div style="font-size:10.5px;color:#8AA88A;margin-bottom:10px;text-align:right">등록일 ${date}</div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
                <button onclick="window.__twDetailSite('${encodeURIComponent(f.name || '')}')" style="
                    padding:7px;background:#1E8B53;color:#fff;border:none;
                    border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;
                    font-family:Pretendard,-apple-system,sans-serif;">🔍 상세보기</button>
                <button onclick="window.__twDeleteSite('${site.id}')" style="
                    padding:7px;background:#FEE2E2;color:#DC2626;border:1.5px solid #FECACA;
                    border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;
                    font-family:Pretendard,-apple-system,sans-serif;">🗑️ 삭제</button>
            </div>
        </div>`;
};

function pxDist(
    map: any,
    nm: any,
    a: { lat: number; lng: number },
    b: { lat: number; lng: number },
): number {
    try {
        const proj = map.getProjection();
        const pa = proj.fromCoordToOffset(new nm.LatLng(a.lat, a.lng));
        const pb = proj.fromCoordToOffset(new nm.LatLng(b.lat, b.lng));
        return Math.sqrt((pa.x - pb.x) ** 2 + (pa.y - pb.y) ** 2);
    } catch {
        return Infinity;
    }
}

type MapType = 'NORMAL' | 'SATELLITE';

interface DemoMapProps {
    drawingMode?: boolean;
    onDrawingComplete?: (pts: { lat: number; lng: number }[]) => void;
    savedSites?: SavedSite[];
    onDeleteSite?: (id: string) => void;
}

export default function DemoMap({
    drawingMode = false,
    onDrawingComplete,
    savedSites = [],
    onDeleteSite,
}: DemoMapProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    const iwRef = useRef<any>(null);
    const navigate = useNavigate();
    const [mapType, setMapType] = useState<MapType>('SATELLITE');

    /* drawing */
    const drawPtsRef = useRef<{ lat: number; lng: number }[]>([]);
    const drawMkrsRef = useRef<any[]>([]);
    const drawLineRef = useRef<any>(null);
    const drawPolyRef = useRef<any>(null);
    const drawEvtRef = useRef<any>(null);
    const onCompleteRef = useRef(onDrawingComplete);
    const onDeleteRef = useRef(onDeleteSite);
    const [ptCount, setPtCount] = useState(0);

    /* saved site Naver layers (border + click) */
    const savedSitesRef = useRef<SavedSite[]>(savedSites);
    const savedLayersRef = useRef<{ poly: any; labelMkr: any }[]>([]);

    /* status filter */
    const [filterStatus, setFilterStatus] = useState<MoistStatus | null>(null);
    const filterStatusRef = useRef<MoistStatus | null>(null);


    useEffect(() => {
        onCompleteRef.current = onDrawingComplete;
    }, [onDrawingComplete]);
    useEffect(() => {
        onDeleteRef.current = onDeleteSite;
    }, [onDeleteSite]);
    useEffect(() => {
        savedSitesRef.current = savedSites;
    }, [savedSites]);

    const switchMapType = (type: MapType) => {
        if (!mapRef.current || !window.naver?.maps) return;
        mapRef.current.setMapTypeId(window.naver.maps.MapTypeId[type]);
        setMapType(type);
    };


    /* ── Naver border polygons + label markers (click detection) ── */
    const renderSavedSites = useCallback(() => {
        if (!mapRef.current || !window.naver?.maps) return;
        const nm = window.naver.maps;
        const map = mapRef.current;

        savedLayersRef.current.forEach(({ poly, labelMkr }) => {
            poly?.setMap(null);
            labelMkr?.setMap(null);
        });
        savedLayersRef.current = [];

        const visibleSites = filterStatusRef.current === null
            ? savedSitesRef.current
            : savedSitesRef.current.filter(
                s => getMoistStatus(s.moist ?? 50) === filterStatusRef.current,
              );

        visibleSites.forEach(site => {
            if (!site.polygon?.length) return;
            const moist = site.moist ?? 50;
            const st = getMoistStatus(moist);
            const color = MOIST_COLOR[st];
            const centroid = calcCentroid(site.polygon);
            const latLngs = site.polygon.map(
                (p: { lat: number; lng: number }) => new nm.LatLng(p.lat, p.lng),
            );

            const poly = new nm.Polygon({
                map,
                paths: latLngs,
                fillColor:   color,
                fillOpacity: 0.18,
                strokeColor:   color,
                strokeWeight:  2.5,
                strokeOpacity: 0.9,
                zIndex: 15,
                clickable: true,
            });

            const { temp, weather } = getSiteWeather(site);
            const labelMkr = new nm.Marker({
                position: new nm.LatLng(centroid.lat, centroid.lng),
                map,
                icon: {
                    content: savedSiteLabelHtml(
                        site.form.name,
                        site.form.area,
                        moist,
                        temp,
                        WEATHER_ICON[weather],
                        site.form.treeName,
                    ),
                    anchor: new nm.Point(55, 20),
                },
                zIndex: 16,
                clickable: true,
            });

            const openIw = (coord: any) => {
                iwRef.current?.close();
                const iw = new nm.InfoWindow({
                    content: savedSiteIwHtml(site),
                    borderWidth: 0,
                    backgroundColor: 'transparent',
                    disableAnchor: true,
                    pixelOffset: new nm.Point(0, -8),
                });
                iw.open(map, coord);
                iwRef.current = iw;
            };

            nm.Event.addListener(poly, 'click', (e: any) => openIw(e.coord));
            nm.Event.addListener(labelMkr, 'click', () =>
                openIw(new nm.LatLng(centroid.lat, centroid.lng)),
            );

            savedLayersRef.current.push({ poly, labelMkr });
        });
    }, []);

    useEffect(() => {
        filterStatusRef.current = filterStatus;
        renderSavedSites();
    }, [savedSites, filterStatus, renderSavedSites]);

    /* ── drawing helpers ── */
    const clearDraw = useCallback(() => {
        drawMkrsRef.current.forEach(m => m?.setMap(null));
        drawMkrsRef.current = [];
        drawLineRef.current?.setMap(null);
        drawLineRef.current = null;
        drawPolyRef.current?.setMap(null);
        drawPolyRef.current = null;
        drawPtsRef.current = [];
        setPtCount(0);
    }, []);

    const completeDraw = useCallback(() => {
        const pts = drawPtsRef.current;
        if (pts.length < 3 || !mapRef.current || !window.naver?.maps) return;
        const nm = window.naver.maps;
        if (drawEvtRef.current) {
            nm.Event.removeListener(drawEvtRef.current);
            drawEvtRef.current = null;
        }

        drawLineRef.current?.setMap(null);
        drawLineRef.current = null;
        drawPolyRef.current = new nm.Polygon({
            map: mapRef.current,
            paths: pts.map(p => new nm.LatLng(p.lat, p.lng)),
            fillColor: '#1E8B53',
            fillOpacity: 0.18,
            strokeColor: '#1E8B53',
            strokeWeight: 2.5,
            strokeOpacity: 1,
            zIndex: 20,
        });

        drawMkrsRef.current.forEach(m => m?.setMap(null));
        drawMkrsRef.current = [];
        mapRef.current.setCursor('');

        const captured = [...pts];
        drawPtsRef.current = [];
        setPtCount(0);
        onCompleteRef.current?.(captured);
    }, []);

    /* ── drawing mode effect ── */
    useEffect(() => {
        if (!mapRef.current || !window.naver?.maps) return;
        const nm = window.naver.maps;

        if (!drawingMode) {
            if (drawEvtRef.current) {
                nm.Event.removeListener(drawEvtRef.current);
                drawEvtRef.current = null;
            }
            clearDraw();
            mapRef.current.setCursor('');
            return;
        }

        mapRef.current.setCursor('crosshair');
        drawEvtRef.current = nm.Event.addListener(
            mapRef.current,
            'click',
            (e: any) => {
                const lat = e.coord.lat(),
                    lng = e.coord.lng();
                const pts = drawPtsRef.current;

                if (
                    pts.length >= 3 &&
                    pxDist(mapRef.current, nm, pts[0], { lat, lng }) < 24
                ) {
                    completeDraw();
                    return;
                }

                pts.push({ lat, lng });
                const n = pts.length;
                setPtCount(n);

                if (n === 3 && drawMkrsRef.current[0]) {
                    drawMkrsRef.current[0].setIcon({
                        content: vtxHtml(true),
                        anchor: new nm.Point(9, 9),
                    });
                }

                const isFirst = n === 1;
                drawMkrsRef.current.push(
                    new nm.Marker({
                        position: new nm.LatLng(lat, lng),
                        map: mapRef.current,
                        icon: {
                            content: isFirst ? vtxHtml(false) : midVtxHtml(),
                            anchor: new nm.Point(
                                isFirst ? 9 : 6,
                                isFirst ? 9 : 6,
                            ),
                        },
                        zIndex: 30,
                        clickable: false,
                    }),
                );

                const path = pts.map(p => new nm.LatLng(p.lat, p.lng));
                if (drawLineRef.current) {
                    drawLineRef.current.setPath(path);
                } else if (path.length >= 2) {
                    drawLineRef.current = new nm.Polyline({
                        map: mapRef.current,
                        path,
                        strokeColor: '#1E8B53',
                        strokeWeight: 2.5,
                        strokeStyle: 'solid',
                        strokeOpacity: 0.9,
                        zIndex: 20,
                    });
                }
            },
        );

        return () => {
            if (drawEvtRef.current) {
                nm.Event.removeListener(drawEvtRef.current);
                drawEvtRef.current = null;
            }
            mapRef.current?.setCursor('');
        };
    }, [drawingMode, clearDraw, completeDraw]);

    /* ── map init ── */
    const initMap = useCallback(() => {
        if (!containerRef.current || !window.naver?.maps || mapRef.current)
            return;
        const nm = window.naver.maps;
        const map = new nm.Map(containerRef.current, {
            center: new nm.LatLng(37.95489785246498, 127.31072100510863),
            zoom: 17,
            minZoom: 6,
            maxZoom: 21,
            mapTypeId: nm.MapTypeId.SATELLITE,
            mapDataControl: false,
            scaleControl: false,
            logoControl: true,
            zoomControl: true,
            zoomControlOptions: {
                style: nm.ZoomControlStyle.SMALL,
                position: nm.Position.TOP_LEFT,
            },
        });

        new nm.CadastralLayer().setMap(map);
        mapRef.current = map;

        SITES.forEach((site, i) => {
            const color = STATUS_COLOR[site.status];
            const marker = new nm.Marker({
                position: new nm.LatLng(COORDS[i].lat, COORDS[i].lng),
                map,
                icon: {
                    content: markerHtml(color, treeEmoji(site.trees)),
                    anchor: new nm.Point(17, 34),
                },
                title: site.name,
                zIndex:
                    site.status === 'danger'
                        ? 10
                        : site.status === 'warn'
                          ? 8
                          : 5,
            });
            nm.Event.addListener(marker, 'click', () => {
                iwRef.current?.close();
                const iw = new nm.InfoWindow({
                    content: iwHtml(site),
                    borderWidth: 0,
                    backgroundColor: 'transparent',
                    disableAnchor: true,
                    pixelOffset: new nm.Point(0, -36),
                });
                iw.open(map, marker);
                iwRef.current = iw;
            });
            markersRef.current.push(marker);
        });

        nm.Event.addListener(map, 'click', () => iwRef.current?.close());

        renderSavedSites();
    }, [renderSavedSites]);

    /* ── mount / unmount ── */
    useEffect(() => {
        (window as any).__twNav = (enc: string) => {
            iwRef.current?.close();
            navigate(`/demo/sites/${enc}`);
        };
        (window as any).__twIwClose = () => iwRef.current?.close();
        (window as any).__twDetailSite = (enc: string) => {
            iwRef.current?.close();
            navigate(`/demo/sites/${enc}`);
        };
        (window as any).__twDeleteSite = (id: string) => {
            iwRef.current?.close();
            onDeleteRef.current?.(id);
        };

        if (window.naver?.maps) {
            initMap();
        } else {
            const existing = document.querySelector<HTMLScriptElement>(
                'script[src*="openapi.map.naver.com"]',
            );
            if (existing) {
                existing.addEventListener('load', () =>
                    setTimeout(initMap, 100),
                );
            } else {
                const key = import.meta.env.VITE_NCP_API_KEY_ID ?? '';
                const script = document.createElement('script');
                script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${key}&submodules=geocoder,drawing,geometry`;
                script.async = true;
                script.onload = () => setTimeout(initMap, 100);
                document.head.appendChild(script);
            }
        }

        return () => {
            markersRef.current.forEach(m => m?.setMap(null));
            markersRef.current = [];
            savedLayersRef.current.forEach(({ poly, labelMkr }) => {
                poly?.setMap(null);
                labelMkr?.setMap(null);
            });
            savedLayersRef.current = [];
            iwRef.current?.close();
            mapRef.current = null;
            delete (window as any).__twNav;
            delete (window as any).__twIwClose;
            delete (window as any).__twDetailSite;
            delete (window as any).__twDeleteSite;
        };
    }, [initMap, navigate]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

            {/* 상태 필터 버튼 그룹 */}
            {!drawingMode && (
                <div style={{
                    position: 'absolute', top: 12, left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000, display: 'flex', gap: 5,
                    fontFamily: 'Pretendard,-apple-system,sans-serif',
                }}>
                    {([null, 'good', 'caution', 'warn', 'danger'] as (MoistStatus | null)[]).map(st => {
                        const isAll = st === null;
                        const active = filterStatus === st;
                        const color = isAll ? '#374151' : MOIST_COLOR[st!];
                        const count = isAll
                            ? savedSites.length
                            : savedSites.filter(s => getMoistStatus(s.moist ?? 50) === st).length;
                        const label = isAll ? '전체' : MOIST_LABEL[st!];
                        return (
                            <button
                                key={String(st)}
                                onClick={() => setFilterStatus(active ? null : st)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    padding: '5px 10px',
                                    borderRadius: 20,
                                    border: `1.5px solid ${active ? color : 'rgba(255,255,255,0.7)'}`,
                                    background: active ? color : 'rgba(255,255,255,0.88)',
                                    color: active ? '#fff' : color,
                                    fontSize: 11.5, fontWeight: 700,
                                    cursor: 'pointer',
                                    boxShadow: '0 1px 6px rgba(0,0,0,0.18)',
                                    backdropFilter: 'blur(4px)',
                                    transition: 'all 0.15s',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {!isAll && (
                                    <span style={{
                                        width: 7, height: 7, borderRadius: '50%',
                                        background: active ? 'rgba(255,255,255,0.8)' : color,
                                        display: 'inline-block', flexShrink: 0,
                                    }} />
                                )}
                                {label}
                                <span style={{
                                    background: active ? 'rgba(255,255,255,0.25)' : `${color}22`,
                                    color: active ? '#fff' : color,
                                    borderRadius: 10, padding: '0 5px',
                                    fontSize: 10, fontWeight: 800,
                                }}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* 그리기 힌트 오버레이 */}
            {drawingMode && (
                <div
                    style={{
                        position: 'absolute',
                        top: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        background: 'rgba(30,139,83,0.92)',
                        color: '#fff',
                        borderRadius: 8,
                        padding: '7px 16px',
                        fontSize: 12.5,
                        fontWeight: 700,
                        fontFamily: 'Pretendard,-apple-system,sans-serif',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    }}
                >
                    {ptCount === 0
                        ? '지도를 클릭해 첫 번째 꼭지점을 찍으세요'
                        : ptCount < 3
                          ? `꼭지점 ${ptCount}개 · 최소 3개 이상 찍어주세요`
                          : `꼭지점 ${ptCount}개 · 첫 번째 꼭지점 근처를 클릭하면 완성됩니다`}
                </div>
            )}

            {/* 지도 타입 토글 버튼 */}
            <div
                style={{
                    position: 'absolute',
                    bottom: 16,
                    right: 16,
                    zIndex: 1000,
                    display: 'flex',
                    borderRadius: 8,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                    border: '1.5px solid #E2E8E2',
                }}
            >
                {(['NORMAL', 'SATELLITE'] as MapType[]).map(t => (
                    <button
                        key={t}
                        onClick={() => switchMapType(t)}
                        style={{
                            padding: '6px 14px',
                            fontSize: 12,
                            fontWeight: 700,
                            fontFamily: 'Pretendard,-apple-system,sans-serif',
                            border: 'none',
                            cursor: 'pointer',
                            background: mapType === t ? '#1E8B53' : '#fff',
                            color: mapType === t ? '#fff' : '#4A6B4A',
                            transition: 'background 0.15s, color 0.15s',
                        }}
                    >
                        {t === 'NORMAL' ? '일반' : '위성'}
                    </button>
                ))}
            </div>
        </div>
    );
}
